import { useMemo } from "react";
import { useFormContext, useFormState } from "react-hook-form";

import { useEligibilityActivity } from "./eligibilityActivity";

/**
 * Whether the estimate can be created or saved right now — the same condition
 * that disables the footer's actions, so the footer's hint text and the buttons
 * can never disagree.
 *
 * CLE-2971 — never while an eligibility check is pending (see `eligibilityActivity.ts`): the
 * estimate would be created on the previous Service Type's benefits while the re-run's answer is
 * stored but never attached to the order.
 *
 * Save Draft passes `allowPendingEligibility` — it is not gated by a pending check (PR #1364), only by
 * the other terms. Create and Schedule (and the footer hint) use the default.
 */
export function useOrderReadiness({
  allowPendingEligibility = false,
}: { allowPendingEligibility?: boolean } = {}): boolean {
  const { getValues, watch } = useFormContext();
  const { isValid } = useFormState();
  const procedures = watch("serviceDetails");
  const isEditingMessage = watch("isEditingMessage");
  const isCheckingEligibility = useEligibilityActivity();

  return useMemo(() => {
    const hasEmptyCptDescription = getValues()?.serviceDetails?.some(
      (procedure: any) => !procedure.cptDescription.trim().length
    );

    return (
      isValid &&
      !!procedures?.length &&
      !hasEmptyCptDescription &&
      !isEditingMessage &&
      (allowPendingEligibility || !isCheckingEligibility)
    );
  }, [
    getValues,
    isValid,
    procedures,
    isEditingMessage,
    isCheckingEligibility,
    allowPendingEligibility,
  ]);
}
